import { HttpClient } from '@angular/common/http';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { catchError, firstValueFrom, of } from 'rxjs';

export interface VehicleResponse {
  Results: [
    {
      Make: string;
      MakeID: string;
      Model: string;
      ModelID: string;
      Manufacturer: string;
      ManufacturerId: string;
      ModelYear: string;
      VehicleType: string;
      FuelTypePrimary: string;
      FuelInjectionType: string;
      EngineConfiguration: string;
      EngineCylinders: string;
      EngineManufacturer: string;
      EngineModel: string;
      BodyClass: string;
      Doors: string;
      Windows: string;
      OtherEngineInfo: string;
      Trim: string;
      Trim2: string;
      Series: string;
      Series2: string;
    },
  ];
}

export type DecodedVehicle = VehicleResponse['Results'][number];

/** Value keys of the configured fields that each vehicle detail is written to. */
export type VehicleFieldKeys = Partial<Record<PropertySet.VehicleFieldRole, string>>;

/** Looks the VIN up with NHTSA, warning the user about anything it couldn't find. */
export async function decodeVin(http: HttpClient, toastSvc: ToastService, vin: string): Promise<DecodedVehicle | null> {
  const vehicle = await firstValueFrom(
    http.get<VehicleResponse>(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vin}?format=json`).pipe(
      catchError(err => {
        console.error(err);
        return of(null);
      })
    )
  );

  const results = vehicle?.Results?.[0];
  if (!vehicle) {
    toastSvc.show({
      title: 'Vehicle not found',
      text: 'Could not find vehicle. Please check your VIN and try again.',
      type: 'warning',
    });
    return null;
  }

  if (!results) {
    toastSvc.show({
      title: 'Vehicle not found',
      text: 'Could not find vehicle with vin ' + vin,
      type: 'warning',
    });
    return null;
  }
  if (!results.Make) {
    toastSvc.show({
      title: 'Could not find Vehicle Make',
      text: 'Could not find vehicle make with vin ' + vin,
      type: 'warning',
    });
  }
  if (!results.Model) {
    toastSvc.show({
      title: 'Could not find Vehicle Model',
      text: 'Could not find vehicle model with vin ' + vin,
      type: 'warning',
    });
  }
  if (!results.ModelYear) {
    toastSvc.show({
      title: 'Could not find Vehicle Year',
      text: 'Could not find vehicle year with vin ' + vin,
      type: 'warning',
    });
  }

  return results;
}

/** Maps decoded values onto whichever configured fields hold each vehicle detail. */
export function buildVinPatch(
  keys: VehicleFieldKeys,
  results: DecodedVehicle,
  vin: string
): Record<string, string | number | null> {
  const patch: Record<string, string | number | null> = {};
  const set = (key: string | undefined, value: string | number | null) => {
    if (key) patch[key] = value;
  };

  set(keys.vin, vin);
  set(keys.make, results.Make || null);
  set(keys.model, results.Model || null);
  set(keys.year, parseInt(results.ModelYear, 10) || new Date().getFullYear());

  const trimValue = [results.Trim, results.Trim2].filter(Boolean).join(' ').trim();
  if (trimValue) set(keys.trim, trimValue);

  return patch;
}
